<script setup lang="ts">
/**
 * ResidentLoginView — ports resident-login.html.
 *
 * Uses AuthLayout chrome (split-screen). The left "hero" side mirrors the
 * teal-accent navy panel. The right card holds sign-in form + IdP options.
 *
 * MSAL flow: clicking an IdP button → auth.loginResident(idp) → MSAL
 * loginRedirect with domain_hint → CIAM → /resident/auth-callback.
 * Three separate buttons for Email, Google, and Facebook let the user
 * bypass the CIAM combined-flow landing screen entirely.
 */
import { ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useAuth } from '@/stores/auth';

type Idp = 'email' | 'google' | 'facebook';

const auth = useAuth();
const loadingIdp = ref<Idp | null>(null);
const errorMsg = ref<string | null>(null);

async function onSignIn(idp: Idp) {
  loadingIdp.value = idp;
  errorMsg.value = null;
  try {
    await auth.loginResident(idp);
    // MSAL redirects the browser; control does not return.
  } catch (e) {
    loadingIdp.value = null;
    errorMsg.value = "Couldn't reach sign-in. Please try again.";
  }
}
</script>

<template>
  <!-- AuthLayout provides the auth-split wrapper -->
  <div class="auth-split" data-testid="resident-login-view">
    <!-- LEFT: Hero panel -->
    <div class="auth-hero resident">
      <div class="launcher-mark" style="margin-bottom: 24px; display: flex; gap: 10px; align-items: center;">
        <div class="brand-mark">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 11.5L12 4l9 7.5" />
            <path d="M5 10v10h14V10" />
            <circle cx="12" cy="14" r="2.2" fill="currentColor" stroke="none" />
          </svg>
        </div>
        <div>
          <div class="brand-name">HOA Watch</div>
          <div class="brand-sub" style="color: color-mix(in oklch, white, var(--navy-100) 50%);">
            Resident portal
          </div>
        </div>
      </div>

      <div class="hero-title" style="margin: auto 0 16px;">Welcome back, neighbor.</div>
      <p class="hero-sub">
        Track issues you've reported, see what the board has resolved, and get updates
        the moment something changes in our community.
      </p>

      <!-- Neighbor grid (decorative, mirrors prototype) -->
      <div class="neighbor-grid">
        <div class="neighbor-tile">412</div>
        <div class="neighbor-tile">homes</div>
        <div class="neighbor-tile you">your<br>HOA</div>
        <div class="neighbor-tile">since '98</div>
      </div>

      <!-- Update card -->
      <div class="hero-update-card">
        <div class="uc-head">
          <div class="uc-dot" />
          <div class="uc-label">Community</div>
        </div>
        <div class="uc-title">
          Sign in to track your submissions and see board updates in real time.
        </div>
        <div class="uc-meta font-mono tabular-nums">Powered by HOA Watch</div>
      </div>
    </div>

    <!-- RIGHT: Sign-in card -->
    <div class="auth-card">
      <h1>Sign in to your account</h1>
      <p class="lead">For residents — sign in with your HOA account.</p>

      <!-- Error banner -->
      <div
        v-if="errorMsg"
        class="p-3 bg-red-50 border border-red-200 rounded-md text-[13.5px] text-red-700 mb-4"
        role="alert"
      >
        {{ errorMsg }}
      </div>

      <!-- Three IdP buttons — each triggers a targeted CIAM domain_hint redirect -->
      <div class="idp-stack" style="margin-top: 16px;">
        <!-- Email / Password -->
        <button
          class="btn btn-primary btn-lg btn-block idp-btn"
          :disabled="loadingIdp !== null"
          data-testid="resident-signin-email"
          @click="onSignIn('email')"
        >
          <!-- Email icon -->
          <svg class="idp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2"/>
            <path d="m3 7 9 6 9-6"/>
          </svg>
          <span v-if="loadingIdp === 'email'">Redirecting…</span>
          <span v-else>Continue with Email</span>
        </button>

        <!-- Google -->
        <button
          class="btn btn-outline btn-lg btn-block idp-btn"
          :disabled="loadingIdp !== null"
          data-testid="resident-signin-google"
          @click="onSignIn('google')"
        >
          <!-- Google "G" mark -->
          <svg class="idp-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          <span v-if="loadingIdp === 'google'">Redirecting…</span>
          <span v-else>Continue with Google</span>
        </button>

        <!-- Facebook -->
        <button
          class="btn btn-outline btn-lg btn-block idp-btn"
          :disabled="loadingIdp !== null"
          data-testid="resident-signin-facebook"
          @click="onSignIn('facebook')"
        >
          <!-- Facebook "f" mark -->
          <svg class="idp-icon" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          <span v-if="loadingIdp === 'facebook'">Redirecting…</span>
          <span v-else>Continue with Facebook</span>
        </button>
      </div>

      <p style="text-align: center; margin-top: 24px; font-size: 13.5px; color: var(--muted);">
        New to your HOA?
        <RouterLink to="/resident/register" style="font-weight: 600; color: var(--navy-700);">
          Create a resident account
        </RouterLink>
      </p>

      <!-- Anonymous portal link -->
      <RouterLink to="/portal" class="anon-link-card" style="text-decoration: none; color: inherit;">
        <div class="ic">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 9V5a3 3 0 0 0-6 0v4" />
            <rect x="3" y="11" width="18" height="10" rx="2" />
          </svg>
        </div>
        <div>
          <div class="nm">Just want to report something quickly?</div>
          <div class="sub">Submit anonymously — no account needed</div>
        </div>
        <div class="arrow">&rarr;</div>
      </RouterLink>

      <!-- Footer links -->
      <div style="margin-top: 40px; padding-top: 24px; border-top: 1px solid var(--border); display: flex; gap: 16px; justify-content: center; font-size: 11.5px; color: var(--muted); flex-wrap: wrap;">
        <a href="#" style="color: inherit;">Privacy</a>
        <a href="#" style="color: inherit;">Terms</a>
        <RouterLink to="/login" style="color: inherit;">Board member sign-in &rarr;</RouterLink>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Resident hero teal accent — mirrors .auth-hero.resident in prototype */
.auth-hero.resident {
  background: radial-gradient(circle at 30% 20%, var(--navy-700), var(--navy-900) 60%);
}
.auth-hero.resident::before {
  background-image:
    radial-gradient(circle at 85% 80%, color-mix(in oklch, var(--teal-500), transparent 55%), transparent 55%),
    radial-gradient(circle at 12% 95%, color-mix(in oklch, var(--teal-600), transparent 70%), transparent 60%);
}

/* Neighbor grid */
.neighbor-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-top: 32px;
  max-width: 460px;
}
.neighbor-tile {
  aspect-ratio: 1;
  border-radius: var(--r-md, 8px);
  background: color-mix(in oklch, white, transparent 88%);
  border: 1px solid color-mix(in oklch, white, transparent 86%);
  display: grid;
  place-items: center;
  font-weight: 600;
  color: color-mix(in oklch, white, var(--navy-100) 30%);
  font-size: 13px;
  text-align: center;
  padding: 4px;
}
.neighbor-tile.you {
  background: color-mix(in oklch, var(--teal-500), transparent 35%);
  border-color: color-mix(in oklch, var(--teal-500), transparent 30%);
  color: white;
  box-shadow: 0 0 0 2px color-mix(in oklch, var(--teal-500), transparent 50%);
}

/* Update card */
.hero-update-card {
  margin-top: auto;
  padding: 18px 18px 16px;
  border-radius: var(--r-lg, 12px);
  background: color-mix(in oklch, white, transparent 92%);
  border: 1px solid color-mix(in oklch, white, transparent 88%);
  backdrop-filter: blur(8px);
}
.hero-update-card .uc-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.hero-update-card .uc-dot {
  width: 8px;
  height: 8px;
  background: var(--green-600);
  border-radius: 50%;
  box-shadow: 0 0 0 4px color-mix(in oklch, var(--green-600), transparent 70%);
}
.hero-update-card .uc-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: color-mix(in oklch, white, var(--teal-100) 40%);
}
.hero-update-card .uc-title { color: white; font-size: 14.5px; font-weight: 600; line-height: 1.45; }
.hero-update-card .uc-meta { color: color-mix(in oklch, white, var(--navy-100) 50%); font-size: 12px; margin-top: 6px; }

/* IdP button stack */
.idp-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.idp-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.idp-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

/* Anonymous link card */
.anon-link-card {
  margin-top: 24px;
  padding: 14px 16px;
  border-radius: var(--r-md, 8px);
  border: 1px dashed var(--border-strong);
  background: var(--bg-soft);
  display: flex;
  align-items: center;
  gap: 12px;
}
.anon-link-card .ic {
  width: 32px;
  height: 32px;
  border-radius: var(--r-sm, 4px);
  background: var(--surface);
  border: 1px solid var(--border);
  color: var(--muted);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.anon-link-card .ic svg { width: 16px; height: 16px; }
.anon-link-card .nm { font-size: 13px; font-weight: 600; }
.anon-link-card .sub { font-size: 12px; color: var(--muted); margin-top: 1px; }
.anon-link-card .arrow { margin-left: auto; color: var(--navy-700); font-weight: 600; font-size: 13px; }
</style>
